import { useCallback, useMemo, useRef } from 'react';

import { chartThemeTokens } from 'design-system';

import { DEFAULT_CHART_HEIGHT, TABLE_ROW_HEIGHT_PX } from '@/config/constants';
import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import useBaseChartOptions from '@/features/charts/hooks/options/useBaseChartOptions';
import getRegularTooltip from '@/features/charts/hooks/tooltip/getRegularTooltip';
import useSpotTooltipFormatter from '@/features/charts/hooks/tooltip/useSpotTooltipFormatter';
import usePrefersReducedMotion from '@/features/charts/hooks/usePrefersReducedMotion';
import { createLastPriceLabel } from '@/features/charts/lib/createLastPriceLabel';
import { applySeriesVisibility } from '@/features/charts/lib/seriesVisibility';
import {
  createChartAppearance,
  type ChartAppearance
} from '@/features/charts/theme/chartAppearance';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/features/charts/types/highcharts';

import useChartFunctions, {
  getStandardWatermarkAttributes,
  getWatermarkConfig
} from './useChartFunctions';

import type Highcharts from 'highcharts/highstock';

export const useRegularOptionsWithAppearance = (
  height: number | undefined,
  isDhChart: boolean,
  appearance: ChartAppearance,
  prefersReducedMotion: boolean
) => {
  const createTooltipFormatter = useSpotTooltipFormatter(
    isDhChart ? 'Alias' : undefined,
    appearance
  );
  const { hasPermission } = usePermission();
  const useSmallWatermark = hasPermission(Permission.UseSmallChartWatermark);

  const tooltip = useMemo(
    () => getRegularTooltip(createTooltipFormatter, appearance, prefersReducedMotion).tooltip,
    [appearance, createTooltipFormatter, prefersReducedMotion]
  );

  const { createBaseChartOptions } = useBaseChartOptions(
    height ? height * TABLE_ROW_HEIGHT_PX + 40 : DEFAULT_CHART_HEIGHT,
    appearance,
    prefersReducedMotion
  );

  const { createEventOptions } = useChartFunctions(appearance, prefersReducedMotion);
  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);

  const { events, exportEvents } = useMemo(() => {
    const { aspectRatio } = getWatermarkConfig(appearance.watermark);
    return createEventOptions(
      watermarkImage,
      chart => getStandardWatermarkAttributes({ chart, aspectRatio, useSmallWatermark }),
      appearance.watermark
    );
  }, [appearance.watermark, createEventOptions, useSmallWatermark]);

  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[] = []): ExtendedOptions => {
      const visibleSeries = applySeriesVisibility(chartSeries, hiddenSeriesIds) ?? chartSeries;
      const baseOptions = createBaseChartOptions(visibleSeries);

      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, events },
        plotOptions: {
          ...baseOptions.plotOptions,
          series: {
            ...baseOptions.plotOptions?.series,
            dataLabels: {
              position: 'right',
              enabled: true,
              useHTML: true,
              formatter(): string | undefined {
                return createLastPriceLabel(this, appearance.accentForeground);
              }
            }
          }
        },
        exporting: {
          ...baseOptions.exporting,
          chartOptions: {
            ...baseOptions.exporting?.chartOptions,
            chart: { events: exportEvents, backgroundColor: appearance.background },
            plotOptions: { series: { dataLabels: { enabled: false } } }
          },
          sourceWidth: undefined,
          sourceHeight: undefined,
          buttons: undefined
        },
        stockTools: { gui: { enabled: false } },
        tooltip,
        series: visibleSeries
      };
    },
    [appearance, createBaseChartOptions, events, exportEvents, tooltip]
  );

  return { createOptions };
};

const useRegularOptions = (height?: number, isDhChart = false) => {
  const mode = 'regular' as const;
  const appearance = useMemo(() => createChartAppearance(chartThemeTokens, mode), [mode]);
  const prefersReducedMotion = usePrefersReducedMotion();

  return useRegularOptionsWithAppearance(height, isDhChart, appearance, prefersReducedMotion);
};

export default useRegularOptions;
